import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type ParsedRow = Record<string, string | number | boolean | null>;

export const createImportJob = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string; kind: string; filename: string; rows: ParsedRow[]; mapping: Record<string, string> }) => d)
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any; const { userId } = context;
    const { data: job, error } = await supabase.from("import_jobs").insert({
      school_id: data.schoolId, kind: data.kind, filename: data.filename,
      created_by: userId, status: "validating", rows_total: data.rows.length,
    }).select().single();
    if (error) throw error;

    const staged = data.rows.map((row, idx) => {
      const errs: { field: string; message: string }[] = [];
      const full_name = String(row[data.mapping.full_name] ?? "").trim();
      const class_label = String(row[data.mapping.class_label] ?? "").trim();
      if (!full_name) errs.push({ field: "full_name", message: "Full name is required" });
      if (full_name.length > 120) errs.push({ field: "full_name", message: "Name too long" });
      return {
        import_job_id: job.id,
        row_index: idx,
        raw_data: row as any,
        full_name,
        student_no: row[data.mapping.student_no] ? String(row[data.mapping.student_no]) : null,
        class_label: class_label || null,
        guardian_phone: row[data.mapping.guardian_phone] ? String(row[data.mapping.guardian_phone]) : null,
        guardian_email: row[data.mapping.guardian_email] ? String(row[data.mapping.guardian_email]) : null,
        bus_route: row[data.mapping.bus_route] ? String(row[data.mapping.bus_route]) : null,
        boarding: /board|yes|true|1/i.test(String(row[data.mapping.boarding] ?? "")),
        validation_status: errs.length ? "error" : "ok",
        validation_errors: errs,
      };
    });

    for (let i = 0; i < staged.length; i += 200) {
      const chunk = staged.slice(i, i + 200);
      const { error: e2 } = await supabase.from("import_staging_students").insert(chunk as any);
      if (e2) throw e2;
    }

    const errCount = staged.filter(s => s.validation_status === "error").length;
    await supabase.from("import_jobs").update({
      status: "ready", rows_ok: staged.length - errCount, rows_error: errCount,
    }).eq("id", job.id);

    return { jobId: job.id, rows: staged.length, errors: errCount };
  });

export const commitImportJob = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { jobId: string }) => d)
  .handler(async ({ data, context }) => {
    const { error, data: r } = await (context.supabase.rpc as any)("commit_student_import", { _job_id: data.jobId });
    if (error) throw new Error(error.message);
    return r;
  });

export const listImportJobs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: jobs } = await (context.supabase as any).from("import_jobs").select("*").eq("school_id", data.schoolId).order("created_at", { ascending: false }).limit(20);
    return jobs ?? [];
  });

export const getStagingPreview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { jobId: string; limit?: number }) => d)
  .handler(async ({ data, context }) => {
    const { data: rows } = await (context.supabase as any).from("import_staging_students").select("*")
      .eq("import_job_id", data.jobId).order("row_index").limit(data.limit ?? 30);
    return rows ?? [];
  });
